import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import { Button } from "gatsby-material-ui-components";
import CheckIcon from "@material-ui/icons/Done";
import ArrowButton from "../common/ArrowButton";
import randomKey from "../../helpers/randomKey";

const useStyles = makeStyles((theme) => ({
  fItem: {
    paddingTop: "2px",
  },
  arrow: {
    color: "#377dff",
    marginTop: "2px",
  },
  arrowBtnLabel: {
    color: "#377dff",
  },
  check: {
    color: "green",
  },
  noMargin: {
    margin: 0,
  },
  paddingLeftRegular: {
    marginLeft: theme.layout.paddingLeftRegular,
  },
  lightTheme: {
    color: "#FFF",
  },
  darkTheme: {
    color: "#000",
  },
  blockCentered: {
    margin: "auto",
  },
  tagWrapper: {
    marginBottom: theme.spacing(1),
  },
  tag: {
    display: "inline-box",
    padding: "2px 4px",
    borderRadius: 3,
    backgroundColor: "rgba(77, 161, 255, 0.14)",
    color: theme.palette.primary.dark,
  },
  withMt: {
    marginTop: "15px",
  },
}));

const TitreTexteCta = ({
  title,
  text,
  ctaUrl,
  items,
  buttonLabel,
  withPaddingLeft,
  tag,
  xs,
  sm,
  md,
  lg,
  btnColor,
  btnSize,
  btnVariant,
  lightTheme,
  darkTheme,
  target,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Grid
      item
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      container
      direction="column"
      alignItems={matches ? "stretch" : "center"}
      spacing={4}
      className={clsx({
        [classes.paddingLeftRegular]: matches && withPaddingLeft,
        [classes.lightTheme]: lightTheme,
        [classes.blockCentered]: !matches,
      })}
    >
      <Grid item>
        {tag && (
          <div className={classes.tagWrapper}>
            <span className={classes.tag}>{tag}</span>
          </div>
        )}
        <Typography
          variant="h1"
          className={clsx(classes.noMargin, {
            [classes.lightTheme]: lightTheme,
          })}
        >
          {title}
        </Typography>
      </Grid>
      <Grid item>
        <Typography
          variant="body1"
          color="textPrimary"
          className={clsx(classes.noMargin, {
            [classes.lightTheme]: lightTheme,
            [classes.darkTheme]: darkTheme,
          })}
        >
          {text}
        </Typography>
      </Grid>
      {items && items.length > 0 && (
        <Grid item container direction="row">
          {items.map((i) => (
            <Grid
              xs={6}
              key={`totreTextCTA-${i}-${randomKey()}`}
              item
              container
              direction="row"
              justify="flex-start"
            >
              <Grid item xs={2}>
                <CheckIcon className={classes.check} />
              </Grid>
              <Grid item className={classes.fItem}>
                {i}
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
      <Grid item>
        {btnVariant !== "text" ? (
          <Button
            to={target === "_self" ? ctaUrl : null}
            href={target === "_blank" ? ctaUrl : null}
            color={btnColor}
            size={btnSize}
            variant={btnVariant}
            className={clsx({ [classes.withMt]: btnVariant !== "text" })}
            target={target}
          >
            {buttonLabel}
          </Button>
        ) : (
          <ArrowButton
            color={btnColor ? btnColor : "primary"}
            label={buttonLabel}
            to={ctaUrl}
            href={ctaUrl}
          />
        )}
      </Grid>
    </Grid>
  );
};

TitreTexteCta.defaultProps = {
  items: [],
  withPaddingLeft: false,
  xs: 12,
  md: 4,
  sm: 4,
  lg: 4,
  btnColor: "primary",
  btnSize: "large",
  btnVariant: "contained",
  lightTheme: false,
  darkTheme: false,
  target: "_self",
};

TitreTexteCta.propTypes = {
  withPaddingLeft: PropTypes.bool.isRequired,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  text: PropTypes.string.isRequired,
  tag: PropTypes.string,
  ctaUrl: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  xs: PropTypes.number.isRequired,
  sm: PropTypes.number.isRequired,
  lg: PropTypes.number.isRequired,
  btnColor: PropTypes.string.isRequired,
  btnSize: PropTypes.string.isRequired,
  btnVariant: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  lightTheme: PropTypes.bool.isRequired,
  darkTheme: PropTypes.bool.isRequired,
};

export default TitreTexteCta;
